import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { Select } from '@/components/live';
import { NAME_MATCH_BASES, nameMatchExample, suggestNameMatchBasis } from '@/lib/nameMatch';

/**
 * "What confirms it is the same person", with an (i) that lists every choice and an example of
 * each (owner, 2026-09-27: "give me examples, add i button to this?", "for each?"). Once a choice
 * is made, its own example sits under it.
 */
export function NameMatchBasisSelect({ value, onChange, disabled, placeholder = 'Select what confirms it', recorded, reference }: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** The two names compared. When given, the likely reason is chosen for the officer to confirm. */
  recorded?: string | null;
  reference?: string | null;
}) {
  const [help, setHelp] = useState(false);
  const example = nameMatchExample(value);
  // Suggest only while the choice is empty or still the last suggestion, so a reason the officer
  // picked is never overwritten as they finish typing the name.
  const suggested = useRef<string | null>(null);
  const suggestion = disabled ? undefined : suggestNameMatchBasis(recorded, reference);
  useEffect(() => {
    if (!suggestion || (value && value !== suggested.current)) return;
    if (value !== suggestion) onChange(suggestion);
    suggested.current = suggestion;
  }, [suggestion, value, onChange]);
  const isSuggested = Boolean(value) && value === suggested.current;
  return (
    <div className="name-match-basis">
      <div className="select-with-add">
        <Select disabled={disabled} onChange={onChange} options={NAME_MATCH_BASES} placeholder={placeholder} value={value} />
        <button aria-expanded={help} aria-label="Examples of each choice" className="icon-only"
                onClick={(event) => { event.preventDefault(); setHelp((now) => !now); }}
                title="Examples of each choice" type="button">
          <Info size={16} />
        </button>
      </div>
      {example && !help && (
        <small className="muted">{isSuggested ? 'Suggested from the two names - change it if it is wrong. ' : ''}e.g. {example}</small>
      )}
      {help && (
        <ul className="name-match-help">
          {NAME_MATCH_BASES.map((entry) => (
            <li className={entry.id === value ? 'is-chosen' : undefined} key={entry.id}>
              <strong>{entry.label}</strong>
              <span>{entry.example}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
